import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../App';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { ChevronUp, Plus, Pin, Sparkles, MessageSquarePlus, X, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

const STATUS_LABELS = {
  under_review: { label: 'Under review', tone: 'bg-stone-100 text-stone-600 border-stone-200' },
  planned: { label: 'Planned', tone: 'bg-amber-50 text-amber-700 border-amber-200' },
  in_development: { label: 'In development', tone: 'bg-blue-50 text-blue-700 border-blue-200' },
  released: { label: 'Released', tone: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
};

/**
 * Feature Requests board, role-scoped.
 *
 * Drop into a tab on either ProviderDashboard or ClientDashboard. The
 * backend automatically filters the pool to the caller's role, so the
 * component itself doesn&rsquo;t need a role prop, it just renders what
 * the API returns.
 *
 * Sort is &lsquo;top&rsquo; by default (most-upvoted first) with a Newest toggle.
 * Submission form is collapsed by default to keep the board feeling
 * like a place to read, not to dump.
 */
export default function FeatureRequestsSection({ helperText }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [sort, setSort] = useState('top');
  const [form, setForm] = useState({ title: '', description: '', why_useful: '' });
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async (currentSort = sort) => {
    setLoading(true);
    try {
      const res = await api.get(`/feature-requests?sort=${currentSort}`);
      setItems(res.data || []);
    } catch {
      setItems([]);
    } finally { setLoading(false); }
  }, [sort]);

  useEffect(() => { load(sort); }, [load, sort]);

  const handleSubmit = async () => {
    if (form.title.trim().length < 5) {
      toast.error('Title needs at least 5 characters');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/feature-requests', form);
      toast.success('Thanks, your request is now on the board');
      setForm({ title: '', description: '', why_useful: '' });
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not submit request');
    } finally { setSubmitting(false); }
  };

  const handleUpvote = async (item) => {
    // Optimistic flip
    const before = items;
    setItems(items.map(i => i.request_id === item.request_id
      ? { ...i, has_upvoted: !i.has_upvoted, upvote_count: i.upvote_count + (i.has_upvoted ? -1 : 1) }
      : i,
    ));
    try {
      if (item.has_upvoted) {
        await api.delete(`/feature-requests/${item.request_id}/upvote`);
      } else {
        await api.post(`/feature-requests/${item.request_id}/upvote`);
      }
    } catch (err) {
      setItems(before);
      toast.error(err.response?.data?.detail || 'Could not register vote');
    }
  };

  return (
    <div className="card-surface p-6" data-testid="feature-requests-section">
      <div className="flex items-start justify-between mb-2 gap-3 flex-wrap">
        <div>
          <h3 className="font-heading text-lg font-medium text-[#1A1A1A] flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-[#D4AF37]" />
            Feature requests
          </h3>
          <p className="text-xs text-stone-500 mt-1">
            {helperText || 'Tell us what would make Zuriya more useful. Upvote others so we know what matters most.'}
            {' '}
            <Link
              to="/roadmap"
              className="text-[#D4AF37] underline inline-flex items-center gap-0.5"
              data-testid="feature-requests-view-roadmap"
            >
              View roadmap<ExternalLink className="h-3 w-3" />
            </Link>
          </p>
        </div>
        <Button
          size="sm"
          onClick={() => setShowForm(s => !s)}
          className="btn-primary gap-1"
          data-testid="feature-request-new-btn"
        >
          {showForm ? <X className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
          {showForm ? 'Close' : 'New request'}
        </Button>
      </div>

      {showForm && (
        <div className="mt-4 p-4 border border-[#E6E4DD] rounded-lg bg-white space-y-3" data-testid="feature-request-form">
          <div>
            <Label className="text-xs">Title</Label>
            <Input
              value={form.title}
              onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))}
              placeholder="Short summary, e.g. 'Bulk add availability slots'"
              maxLength={120}
              className="input-zuriya"
              data-testid="feature-request-title"
            />
          </div>
          <div>
            <Label className="text-xs">Description (optional)</Label>
            <Textarea
              value={form.description}
              onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="What would it do? How would it work?"
              maxLength={2000}
              rows={3}
              className="input-zuriya"
              data-testid="feature-request-description"
            />
          </div>
          <div>
            <Label className="text-xs">Why is this useful?</Label>
            <Textarea
              value={form.why_useful}
              onChange={(e) => setForm(f => ({ ...f, why_useful: e.target.value }))}
              placeholder="The problem it solves, or the time/money it would save you"
              maxLength={1000}
              rows={2}
              className="input-zuriya"
              data-testid="feature-request-why"
            />
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" size="sm" onClick={() => setShowForm(false)} className="border-stone-200">
              Cancel
            </Button>
            <Button size="sm" onClick={handleSubmit} disabled={submitting} className="btn-primary" data-testid="feature-request-submit">
              {submitting ? 'Submitting\u2026' : 'Submit request'}
            </Button>
          </div>
        </div>
      )}

      <div className="mt-4 flex items-center gap-2 text-xs">
        <span className="text-stone-500">Sort:</span>
        <button
          type="button"
          onClick={() => setSort('top')}
          className={`px-2 py-0.5 rounded-full border ${sort === 'top' ? 'bg-[#1A1A1A] text-white border-[#1A1A1A]' : 'bg-white border-[#E6E4DD] text-stone-600'}`}
          data-testid="feature-request-sort-top"
        >
          Top
        </button>
        <button
          type="button"
          onClick={() => setSort('new')}
          className={`px-2 py-0.5 rounded-full border ${sort === 'new' ? 'bg-[#1A1A1A] text-white border-[#1A1A1A]' : 'bg-white border-[#E6E4DD] text-stone-600'}`}
          data-testid="feature-request-sort-new"
        >
          Newest
        </button>
      </div>

      <ul className="mt-4 space-y-2" data-testid="feature-request-list">
        {loading && <p className="text-xs text-stone-400">Loading&hellip;</p>}
        {!loading && items.length === 0 && (
          <li className="text-center py-8 border border-dashed border-[#E6E4DD] rounded-lg">
            <MessageSquarePlus className="h-6 w-6 text-stone-300 mx-auto mb-2" />
            <p className="text-sm text-stone-500">No requests yet.</p>
            <p className="text-xs text-stone-400 mt-1">Be the first to suggest something.</p>
          </li>
        )}
        {!loading && items.map(item => {
          const status = STATUS_LABELS[item.status] || STATUS_LABELS.under_review;
          return (
            <li
              key={item.request_id}
              className="flex items-start gap-3 p-3 bg-[#F9F6F0] rounded-lg"
              data-testid={`feature-request-row-${item.request_id}`}
            >
              <button
                type="button"
                onClick={() => handleUpvote(item)}
                disabled={item.is_mine}
                className={`flex flex-col items-center justify-center w-12 h-12 shrink-0 rounded-lg border transition-colors ${
                  item.has_upvoted
                    ? 'bg-[#D4AF37] text-white border-[#D4AF37]'
                    : 'bg-white border-[#E6E4DD] text-stone-600 hover:border-[#D4AF37] hover:text-[#D4AF37]'
                } ${item.is_mine ? 'opacity-60 cursor-not-allowed' : ''}`}
                data-testid={`upvote-btn-${item.request_id}`}
                title={item.is_mine ? "You can't downvote your own request" : (item.has_upvoted ? 'Remove your upvote' : 'Upvote this')}
              >
                <ChevronUp className="h-4 w-4" />
                <span className="text-xs font-medium">{item.upvote_count}</span>
              </button>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  {item.pinned && <Pin className="h-3 w-3 text-[#D4AF37]" />}
                  <p className="text-sm font-medium text-[#1A1A1A]">{item.title}</p>
                  <span className={`text-[10px] font-medium border px-1.5 py-0.5 rounded-full ${status.tone}`}>
                    {status.label}
                  </span>
                  {item.is_mine && (
                    <span className="text-[10px] font-medium text-[#D4AF37] bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded-full">
                      Your request
                    </span>
                  )}
                </div>
                {item.description && (
                  <p className="text-xs text-stone-600 mt-1 whitespace-pre-line">{item.description}</p>
                )}
                {item.why_useful && (
                  <p className="text-xs text-stone-500 mt-1 italic">&ldquo;{item.why_useful}&rdquo;</p>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
